import React, { useState, useEffect } from "react";
import {
  Typography, Box, Paper, Tabs, Tab, TextField, Button, Grid,
  FormControl, FormControlLabel,
  Checkbox, Alert,
  Autocomplete, Dialog,
  DialogTitle, DialogContent, DialogContentText, DialogActions, CircularProgress
} from "@mui/material";
// import { Add, Remove } from "@mui/icons-material";
import { proveedores } from "../../api/proveedores";
import { product } from "../../api/paquetes";
import { useNotification } from "../../context/notification.context";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";
import { ServiceTable } from "./Sections/ServiceTable";

// Opciones predefinidas para autocomplete (constantes fuera del componente para mejor performance)
const OPCIONES_ORIGEN = [
  "San Juan", "Buenos Aires", "CABA", "Córdoba", "Mendoza", "Santa Fe", "Tucumán", "Entre Ríos", "Salta", "Misiones", "Chaco", "Santiago del Estero", "Corrientes", "La Pampa", "Jujuy", "Río Negro", "Neuquén", "Catamarca", "San Luis", "La Rioja", 
  "Santiago de Chile", "Bogotá", "Lima", "Ciudad de México", "Montevideo", "Brasilia",
  "Nueva York", "Londres", "París", "Tokio", "Dubái", "Los Ángeles", "Roma", "Madrid", "Seúl", "Ámsterdam", "Miami", "Barcelona", "Sao Paulo", "Toronto", "Rio de Janeiro"
]

const OPCIONES_DESTINO = [
  "San Juan", "Buenos Aires", "CABA", "Córdoba", "Mendoza", "Santa Fe", "Tucumán", "Entre Ríos", "Salta", "Misiones", "Chaco", "Santiago del Estero", "Corrientes", "La Pampa", "Jujuy", "Río Negro", "Neuquén", "Catamarca", "San Luis", "La Rioja", 
  "Santiago de Chile", "Bogotá", "Lima", "Ciudad de México", "Montevideo", "Brasilia",
  "Nueva York", "Londres", "París", "Tokio", "Dubái", "Los Ángeles", "Roma", "Madrid", "Seúl", "Ámsterdam", "Miami", "Barcelona", "Sao Paulo", "Toronto", "Rio de Janeiro"
]



// Interfaces para tipado
type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

type BaseCompra = {
  proveedor: string;
  monedaCosto: string; // USD | ARS
  precioCosto: number;
  nroFile?: string; // opcional, editable luego
};

type ServicioAereo = {
  id: string;
  aerolinea: string;
  destino: string;
  descripcion: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

type ServicioAlojamiento = {
  id: string;
  hotel: string;
  destino: string;
  noches: number;
  serviciosIncluidos: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

type ServicioExtra = {
  id: string;
  servicio: string;
  moneda: string;
  precioVenta: number;
  compra: BaseCompra;
};

// Componente para paneles de pestañas
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div role="tabpanel" hidden={value !== index} id={`simple-tabpanel-${index}`} aria-labelledby={`simple-tab-${index}`} {...other}>
    {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
  </div>
);

const NuevoPresupuesto: React.FC<{ onPackageCreated?: () => void }> = ({ onPackageCreated }) => {
  const [pestañaActiva, setPestañaActiva] = useState(0);
  const [guardando, setGuardando] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  
  // Estado principal del formulario
  const [datosFormulario, setDatosFormulario] = useState({
    concepto: "", origen: "", destino: "", fechaSalida: null as Dayjs | null,
    adultos: 0, ninos: 0, infantes: 0, descripcion: "",
    cliente: "", telefonoCliente: "", emailCliente: "", obraSocial: false
  });
  
  const [errores, setErrores] = useState<{[key: string]: string}>({});
  
  // Estados para los servicios
  const [serviciosAereos, setServiciosAereos] = useState<ServicioAereo[]>([]);
  const [serviciosAlojamiento, setServiciosAlojamiento] = useState<ServicioAlojamiento[]>([]);
  const [serviciosExtras, setServiciosExtras] = useState<ServicioExtra[]>([]);
  
  // Totales calculados
  const [totalARS, setTotalARS] = useState(0);
  const [totalUSD, setTotalUSD] = useState(0);

  // Proveedores desde API
  const [opcionesProveedores, setOpcionesProveedores] = useState<string[]>([]);
  const [proveedoresCompletos, setProveedoresCompletos] = useState<any[]>([]);
  const [opcionesAereolineas, setOpcionesAereolineas] = useState<string[]>([]);
  const [opcionesHoteles, setOpcionesHoteles] = useState<string[]>([]);
  const { getError, getSuccess } = useNotification();

  // Cargar proveedores reales desde API (solo nombres) al montar
  useEffect(() => {
    const cargarProveedores = async () => {
      try {
        const pageSize = 500;
        let page = 1;
        let keepLoading = true;
        const acumulados: any[] = [];

        while (keepLoading) {
          const response = await proveedores.getAll(page, pageSize);
          const payload = response.data;
          const dataArr: any[] = Array.isArray(payload?.data)
            ? payload.data
            : Array.isArray(payload)
              ? payload
              : [];

          acumulados.push(...dataArr);

          const hasNext = Boolean(payload?.pagination?.hasNextPage);
          if (!hasNext || dataArr.length === 0) {
            keepLoading = false;
          } else {
            page += 1;
          }
        }

        const seenIds = new Set<number>();
        const providersList = acumulados
          .map((p: any) => ({ id: Number(p.id), nombre: (p?.nombre || '').toString().trim() }))
          .filter((p) => Number.isFinite(p.id) && !!p.nombre)
          .filter((p) => {
            if (seenIds.has(p.id)) return false;
            seenIds.add(p.id);
            return true;
          });

        setProveedoresCompletos(providersList);

        const nombres: string[] = providersList.map((p: any) => p.nombre).filter((n: string) => !!n);
        const unicos: string[] = Array.from(new Set(nombres));
        setOpcionesProveedores(unicos);

        // Heurística para separar aerolíneas y hoteles
        const aeroRegex = /\b(?:aerolineas|airlines|air|fly|jet|latam|copa|jetsmart|flybondi|easy jet|jet blue)\b/i;
        const hotelRegex = /\b(?:hotel|hostel|apart|suite|suites|resort|inn|posada|hosteria|b&b|bed and breakfast|spa|motel)\b/i;

        const aero = unicos.filter(n => aeroRegex.test(n));
        const hoteles = unicos.filter(n => hotelRegex.test(n));

        setOpcionesAereolineas(aero);
        setOpcionesHoteles(hoteles);
      } catch (e) {
        getError("Error al cargar proveedores");
        setOpcionesProveedores([]);
        setOpcionesAereolineas([]);
        setOpcionesHoteles([]);
      }
    };
    cargarProveedores();
  }, [getError]);

  // Handlers para pestañas
  const manejarCambioPestaña = (_event: React.SyntheticEvent, nuevoValor: number) => {
    // Solo permitir ir a la segunda pestaña si la primera está completa
    if (nuevoValor === 1 && !esPrimeraPestañaValida) return;
    setPestañaActiva(nuevoValor);
  };

  // Handler genérico para cambios en inputs
  const manejarCambioInput = (campo: string, valor: any) => {
    setDatosFormulario(prev => ({ ...prev, [campo]: valor }));
    
    // Limpiar error cuando el usuario empieza a escribir
    if (errores[campo]) {
      setErrores(prev => ({ ...prev, [campo]: "" }));
    }
  };

  // Validaciones
  const validarFormulario = () => {
    const nuevosErrores: {[key: string]: string} = {};
    
    if (!datosFormulario.concepto.trim()) nuevosErrores.concepto = "El concepto es obligatorio";
    if (!datosFormulario.origen.trim()) nuevosErrores.origen = "El origen es obligatorio";
    if (!datosFormulario.destino.trim()) nuevosErrores.destino = "El destino es obligatorio";
    if (!datosFormulario.fechaSalida) nuevosErrores.fechaSalida = "La fecha de salida es obligatoria";
    const totalPasajeros = (datosFormulario.adultos || 0) + (datosFormulario.ninos || 0) + (datosFormulario.infantes || 0);
    if (totalPasajeros === 0) {
      nuevosErrores.pasajeros = "Debe haber al menos un pasajero (adulto, niño o infante)";
    }
    
    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  const validarSegundaPestaña = () => {
    const nuevosErrores: {[key: string]: string} = {};
    if (!datosFormulario.cliente.trim()) nuevosErrores.cliente = "El cliente es obligatorio";
    setErrores(nuevosErrores);
    return Object.keys(nuevosErrores).length === 0;
  };

  // Navegación entre pestañas
  const manejarSiguiente = () => {
    if (validarFormulario()) setPestañaActiva(1);
  };

  const confirmarYGuardar = () => {
    if (!validarSegundaPestaña()) return;
    if (!validarServicios()) return;
    setMostrarConfirmacion(true);
  };

  const manejarGuardar = async () => {
    setMostrarConfirmacion(false);
    setGuardando(true);

    try {
      // Validación de servicios y totales antes de enviar
      if (!validarServicios()) {
        setGuardando(false);
        return;
      }
      // Función helper para obtener proveedor_id desde el nombre
      const obtenerProveedorId = (nombreProveedor: string): number | null => {
        const normalize = (s: string) => (s || '').toString().trim().toUpperCase();
        const proveedor = proveedoresCompletos.find((p: any) => normalize(p.nombre) === normalize(nombreProveedor));
        return proveedor ? proveedor.id : null;
      };

      // Transformar datos al formato del backend
      const vendedorNombre = (localStorage.getItem("current_username") || "").trim();
      const usuarioObs = vendedorNombre || "SISTEMA";
      const fechaObs = new Date().toISOString().split('T')[0];

      const buildObs = (lines: string[]) => ({
        descripcion: lines.filter(Boolean).join('\n'),
        fecha: fechaObs,
        usuario: usuarioObs,
      });

      const observacionesAuto: any[] = [];

      // Aéreos
      serviciosAereos.forEach((s) => {
        observacionesAuto.push(buildObs([
          'Nuevo aéreo',
          `Aerolinea: ${s.aerolinea || ''}`,
          `Destino: ${s.destino || ''}`,
          `Descripción: ${s.descripcion || ''}`,
        ]));

        if (typeof s.compra?.precioCosto === 'number' && s.compra.precioCosto > 0) {
          const monedaCosto = s.compra?.monedaCosto || 'USD';
          observacionesAuto.push(buildObs([
            'Nuevo aéreo costo',
            `Aerolinea: ${s.aerolinea || ''}`,
            `Destino: ${s.destino || ''}`,
            `Descripción: ${s.descripcion || ''}`,
            `Costo: ${monedaCosto} ${s.compra.precioCosto}`,
          ]));
        }
      });

      // Hoteles / alojamientos
      serviciosAlojamiento.forEach((s) => {
        observacionesAuto.push(buildObs([
          'Nuevo servicio',
          `Descripción: ${(s.hotel || '').toString()} - ${(s.destino || '').toString()}`,
        ]));

        if (typeof s.compra?.precioCosto === 'number' && s.compra.precioCosto > 0) {
          const monedaCosto = s.compra?.monedaCosto || 'USD';
          observacionesAuto.push(buildObs([
            'Nuevo servicio costo',
            `Descripción: ${(s.hotel || '').toString()} - ${(s.destino || '').toString()}`,
            `Costo: ${monedaCosto} ${s.compra.precioCosto}`,
          ]));
        }
      });

      // Extras
      serviciosExtras.forEach((s) => {
        observacionesAuto.push(buildObs([
          'Nuevo servicio',
          `Descripción: ${s.servicio || ''}`,
        ]));

        if (typeof s.compra?.precioCosto === 'number' && s.compra.precioCosto > 0) {
          const monedaCosto = s.compra?.monedaCosto || 'USD';
          observacionesAuto.push(buildObs([
            'Nuevo servicio costo',
            `Descripción: ${s.servicio || ''}`,
            `Costo: ${monedaCosto} ${s.compra.precioCosto}`,
          ]));
        }
      });

      const datosAPI: any = {
        concepto: datosFormulario.concepto,
        fecha_salida: datosFormulario.fechaSalida ? new Date(datosFormulario.fechaSalida.toDate()).toISOString() : "",
        moneda: "USD", // La moneda del paquete base ya no es limitante, los totales se ven por servicio
        importe: (totalUSD || 0) + (totalARS || 0), // Mandamos la suma (o cualquier valor, el backend lo recalcula)
        descripcion: datosFormulario.descripcion || undefined,
        vendedor_nombre: vendedorNombre || "SISTEMA",
        observaciones: observacionesAuto.length > 0 ? JSON.stringify(observacionesAuto) : undefined,
        adultos: datosFormulario.adultos,
        ninos: datosFormulario.ninos,
        infantes: datosFormulario.infantes,
        obra_social: datosFormulario.obraSocial,
        cliente: {
          nombre: datosFormulario.cliente,
          telefono: datosFormulario.telefonoCliente || undefined,
          email: datosFormulario.emailCliente || undefined,
          celular: datosFormulario.telefonoCliente || undefined,
          correo: datosFormulario.emailCliente || undefined,
        },
      };

      // Solo agregar servicios si hay al menos uno
      if (serviciosAereos.length > 0) {
        datosAPI.aereosPaquetes = serviciosAereos.map(s => ({
          descripcion: `${s.aerolinea} - ${s.destino} - ${s.descripcion}`,
          moneda: s.moneda,
          importe: s.precioVenta,
          moneda_costo: s.compra?.monedaCosto ? s.compra.monedaCosto : undefined,
          importe_costo: typeof s.compra?.precioCosto === 'number' && s.compra.precioCosto > 0 ? s.compra.precioCosto : undefined,
          nro_file: s.compra?.nroFile || undefined,
          proveedor_id: s.compra?.proveedor ? obtenerProveedorId(s.compra.proveedor) : null,
        }));
      }

      if (serviciosAlojamiento.length > 0) {
        datosAPI.hotelesPaquetes = serviciosAlojamiento.map(s => ({
          descripcion: [s.hotel, s.destino, s.noches ? `${s.noches} noches` : '', s.serviciosIncluidos].filter(Boolean).join(' - '),
          noches: s.noches,
          servicios_incluidos: s.serviciosIncluidos || undefined,
          moneda: s.moneda,
          importe: s.precioVenta,
          moneda_costo: s.compra?.monedaCosto ? s.compra.monedaCosto : undefined,
          importe_costo: typeof s.compra?.precioCosto === 'number' && s.compra.precioCosto > 0 ? s.compra.precioCosto : undefined,
          nro_file: s.compra?.nroFile || undefined,
          proveedor_id: s.compra?.proveedor ? obtenerProveedorId(s.compra.proveedor) : null,
        }));
      }

      if (serviciosExtras.length > 0) {
        datosAPI.serviciosPaquetes = serviciosExtras.map(s => ({
          descripcion: s.servicio,
          moneda: s.moneda,
          importe: s.precioVenta,
          moneda_costo: s.compra?.monedaCosto ? s.compra.monedaCosto : undefined,
          importe_costo: typeof s.compra?.precioCosto === 'number' && s.compra.precioCosto > 0 ? s.compra.precioCosto : undefined,
          nro_file: s.compra?.nroFile || undefined,
          proveedor_id: s.compra?.proveedor ? obtenerProveedorId(s.compra.proveedor) : null,
        }));
      }

      const response = await product.create(datosAPI);
      const createdId = response.data?.data?.id || response.data?.id;
      const createdNumero = response.data?.numero || response.data?.data?.numero || createdId;

      const contarElegibles = () => {
        const countFrom = (arr: any[]) => arr.filter((s: any) => {
          const proveedorId = s.compra?.proveedor ? obtenerProveedorId(s.compra.proveedor) : null;
          const hasProvider = Number.isFinite(Number(proveedorId)) && Number(proveedorId) > 0;
          const hasFile = (s.compra?.nroFile || '').toString().trim().length > 0;
          const hasCosto = Number(s.compra?.precioCosto || 0) > 0;
          return hasProvider && hasFile && hasCosto;
        }).length;

        return countFrom(serviciosAereos) + countFrom(serviciosAlojamiento) + countFrom(serviciosExtras);
      };

      // Asegurar persistencia de observaciones automáticas (algunos backends filtran campos en create)
      try {
        if (createdId && observacionesAuto.length > 0) {
          await product.update(createdId, { observaciones: JSON.stringify(observacionesAuto) });
        }
      } catch (e) {
        // No bloquear creación si falla la persistencia de observaciones
        console.warn('No se pudieron persistir observaciones automáticas', e);
      }

      if (createdId && contarElegibles() > 0) {
        try {
          const syncResponse = await product.syncProviders(createdId);
          const result = syncResponse.data || {};
          getSuccess(
            `Paquete ${createdNumero} creado y enviado a proveedores. Creados: ${result.created || 0}, Actualizados: ${result.updated || 0}, Omitidos: ${result.skipped || 0}.`
          );
        } catch (syncError: any) {
          const syncMsg = syncError?.response?.data?.message || 'No se pudo sincronizar con Proveedores';
          getSuccess(`Paquete creado exitosamente con número ${createdNumero}.`);
          getError(`Guardado correcto, pero falló el envío: ${syncMsg}`);
        }
      } else {
        getSuccess(`Paquete creado exitosamente con número ${createdNumero}`);
      }
      
      // Limpiar formulario
      setDatosFormulario({
        concepto: "", origen: "", destino: "", fechaSalida: null,
        adultos: 0, ninos: 0, infantes: 0, descripcion: "",
        cliente: "", telefonoCliente: "", emailCliente: "", obraSocial: false
      });
      setServiciosAereos([]);
      setServiciosAlojamiento([]);
      setServiciosExtras([]);
      setPestañaActiva(0);
      
      // Notificar al componente padre para redirigir
      if (onPackageCreated) {
        onPackageCreated();
      }
      
    } catch (error: any) {
      console.error("Error al crear paquete:", error);
      let mensaje = "Error al crear el paquete";
      
      if (error?.response?.data?.message) {
        mensaje = error.response.data.message;
      } else if (error?.response?.data?.errors) {
        const errores = error.response.data.errors;
        mensaje = Object.values(errores).flat().join(", ");
      } else if (error?.message) {
        mensaje = error.message;
      }
      
      getError(mensaje);
    } finally {
      setGuardando(false);
    }
  };

  // Validación estricta de servicios y totales
  const validarServicios = () => {
    const erroresLocales: {[key: string]: string} = {};

    const totalServicios = serviciosAereos.length + serviciosAlojamiento.length + serviciosExtras.length;
    if (totalServicios === 0) {
      erroresLocales.servicios = "Debe agregar al menos un servicio (aéreo, hotel o extra)";
    }

    // Validar aéreos
    serviciosAereos.forEach((s, idx) => {
      if (!s.aerolinea?.trim()) erroresLocales[`aereo_${idx}_aerolinea`] = "La aerolínea es obligatoria";
      if (!s.destino?.trim()) erroresLocales[`aereo_${idx}_destino`] = "El destino es obligatorio";
      if (!s.moneda) erroresLocales[`aereo_${idx}_moneda`] = "La moneda es obligatoria";
      if (!s.precioVenta || s.precioVenta <= 0) erroresLocales[`aereo_${idx}_precioVenta`] = "El precio de venta debe ser mayor a 0";
    });

    // Validar alojamientos
    serviciosAlojamiento.forEach((s, idx) => {
      if (!s.hotel?.trim()) erroresLocales[`aloj_${idx}_hotel`] = "El hotel es obligatorio";
      if (!s.destino?.trim()) erroresLocales[`aloj_${idx}_destino`] = "El destino es obligatorio";
      if (!s.noches || s.noches <= 0) erroresLocales[`aloj_${idx}_noches`] = "Las noches deben ser al menos 1";
      if (!s.moneda) erroresLocales[`aloj_${idx}_moneda`] = "La moneda es obligatoria";
      if (!s.precioVenta || s.precioVenta <= 0) erroresLocales[`aloj_${idx}_precioVenta`] = "El precio de venta debe ser mayor a 0";
    });

    // Validar extras
    serviciosExtras.forEach((s, idx) => {
      if (!s.servicio?.trim()) erroresLocales[`extra_${idx}_servicio`] = "La descripción del servicio es obligatoria";
      if (!s.moneda) erroresLocales[`extra_${idx}_moneda`] = "La moneda es obligatoria";
      if (!s.precioVenta || s.precioVenta <= 0) erroresLocales[`extra_${idx}_precioVenta`] = "El precio de venta debe ser mayor a 0";
    });

    // Totales: impedir importe 0
    const totalVenta = (totalUSD || 0) + (totalARS || 0);
    if (totalServicios > 0 && totalVenta <= 0) {
      erroresLocales.total = "El importe total del paquete debe ser mayor a 0";
    }

    if (Object.keys(erroresLocales).length > 0) {
      // Unificar con el estado de errores existente sin pisar otros
      setErrores(prev => ({ ...prev, ...erroresLocales }));
      // Notificación genérica
      getError("Revise los servicios: faltan datos obligatorios o importes");
      return false;
    }
    return true;
  };

  // Validación para habilitar segunda pestaña
  const esPrimeraPestañaValida = datosFormulario.concepto && datosFormulario.origen && datosFormulario.destino && 
                         datosFormulario.fechaSalida && (datosFormulario.adultos > 0 || datosFormulario.ninos > 0 || datosFormulario.infantes > 0);

  // Funciones genéricas para manejo de servicios
  const crearServicio = <T extends { id: string }>(tipo: 'aereo' | 'alojamiento' | 'extra'): T => {
    const compraBase: BaseCompra = { proveedor: '', monedaCosto: 'USD', precioCosto: 0 };
    const servicioBase = { id: Date.now().toString(), moneda: "USD", precioVenta: 0, compra: compraBase } as any;
    
    switch (tipo) {
      case 'aereo':
        return { ...servicioBase, aerolinea: "", destino: "", descripcion: "" } as unknown as T;
      case 'alojamiento':
        return { ...servicioBase, hotel: "", destino: "", noches: 0, serviciosIncluidos: "" } as unknown as T;
      case 'extra':
        return { ...servicioBase, servicio: "" } as unknown as T;
      default:
        return servicioBase as unknown as T;
    }
  };

  // Funciones para servicios aéreos
  const agregarServicioAereo = () => {
    setServiciosAereos([...serviciosAereos, crearServicio<ServicioAereo>('aereo')]);
  };

  const eliminarServicioAereo = (id: string) => {
    setServiciosAereos(serviciosAereos.filter(servicio => servicio.id !== id));
  };

  const actualizarServicioAereo = (id: string, campo: keyof ServicioAereo, valor: any) => {
    setServiciosAereos(serviciosAereos.map(servicio => 
      servicio.id === id 
        ? (campo === 'compra' 
            ? { ...servicio, compra: { ...servicio.compra, ...valor } } 
            : { ...servicio, [campo]: valor })
        : servicio
    ));
  };

  // Funciones para servicios de alojamiento
  const agregarServicioAlojamiento = () => {
    setServiciosAlojamiento([...serviciosAlojamiento, crearServicio<ServicioAlojamiento>('alojamiento')]);
  };

  const eliminarServicioAlojamiento = (id: string) => {
    setServiciosAlojamiento(serviciosAlojamiento.filter(servicio => servicio.id !== id));
  };

  const actualizarServicioAlojamiento = (id: string, campo: keyof ServicioAlojamiento, valor: any) => {
    setServiciosAlojamiento(serviciosAlojamiento.map(servicio => 
      servicio.id === id 
        ? (campo === 'compra' 
            ? { ...servicio, compra: { ...servicio.compra, ...valor } } 
            : { ...servicio, [campo]: valor })
        : servicio
    ));
  };

  // Funciones para servicios extras
  const agregarServicioExtra = () => {
    setServiciosExtras([...serviciosExtras, crearServicio<ServicioExtra>('extra')]);
  };

  const eliminarServicioExtra = (id: string) => {
    setServiciosExtras(serviciosExtras.filter(servicio => servicio.id !== id));
  };

  const actualizarServicioExtra = (id: string, campo: keyof ServicioExtra, valor: any) => {
    setServiciosExtras(serviciosExtras.map(servicio => 
      servicio.id === id 
        ? (campo === 'compra' 
            ? { ...servicio, compra: { ...servicio.compra, ...valor } } 
            : { ...servicio, [campo]: valor })
        : servicio
    ));
  };

  // Cálculo de totales
  const [totalCostoARS, setTotalCostoARS] = useState(0);
  const [totalCostoUSD, setTotalCostoUSD] = useState(0);

  const calcularTotales = React.useCallback(() => {
    let totalUSDCalc = 0;
    let totalARSCalc = 0;
    let costoUSDCalc = 0;
    let costoARSCalc = 0;

    // Sumar todos los servicios (venta y costo)
    [...serviciosAereos, ...serviciosAlojamiento, ...serviciosExtras].forEach(servicio => {
      if (servicio.moneda === "USD") {
        totalUSDCalc += servicio.precioVenta;
      } else {
        totalARSCalc += servicio.precioVenta;
      }
      if (servicio.compra?.monedaCosto === "USD") {
        costoUSDCalc += servicio.compra?.precioCosto || 0;
      } else {
        costoARSCalc += servicio.compra?.precioCosto || 0;
      }
    });

    setTotalUSD(totalUSDCalc);
    setTotalARS(totalARSCalc);
    setTotalCostoUSD(costoUSDCalc);
    setTotalCostoARS(costoARSCalc);
  }, [serviciosAereos, serviciosAlojamiento, serviciosExtras]);

  // Actualizar totales cuando cambien los servicios
  React.useEffect(() => {
    calcularTotales();
  }, [calcularTotales]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ mt: 10, maxWidth: 1600, mx: 'auto' }}>
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, ml: 74 }}>
            Nuevo Presupuesto
          </Typography>
      
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, ml: 3 }}>
            <Tabs value={pestañaActiva} onChange={manejarCambioPestaña} aria-label="tabs presupuesto">
              <Tab label="Datos y descripción" />
              <Tab label="Cliente" disabled={!esPrimeraPestañaValida} />
            </Tabs>
          </Box>

          <TabPanel value={pestañaActiva} index={0}>
            <Grid container spacing={3}>
              {/* Concepto y fecha */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth error={!!errores.concepto}>
                  <TextField
                    label="Concepto"
                    multiline
                    rows={1}
                    value={datosFormulario.concepto}
                    onChange={(e) => manejarCambioInput('concepto', e.target.value)}
                    fullWidth
                    placeholder="Ej: Paquete Completo"
                  />
                  {errores.concepto && <Alert severity="error" sx={{ mt: 1 }}>{errores.concepto}</Alert>}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Fecha de salida"
                  format="DD/MM/YYYY"
                  value={datosFormulario.fechaSalida}
                  onChange={(newValue) => manejarCambioInput('fechaSalida', newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errores.fechaSalida
                    }
                  }}
                />
                {errores.fechaSalida && <Alert severity="error" sx={{ mt: 1 }}>{errores.fechaSalida}</Alert>}
              </Grid>

              {/* Origen y destino */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={OPCIONES_ORIGEN}
                  value={datosFormulario.origen}
                  onChange={(event, newValue) => manejarCambioInput('origen', newValue || '')}
                  onInputChange={(event, newInputValue) => manejarCambioInput('origen', newInputValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Origen" error={!!errores.origen} helperText={errores.origen} />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={OPCIONES_DESTINO}
                  value={datosFormulario.destino}
                  onChange={(event, newValue) => manejarCambioInput('destino', newValue || '')}
                  onInputChange={(event, newInputValue) => manejarCambioInput('destino', newInputValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Destino" error={!!errores.destino} helperText={errores.destino} />
                  )}
                />
              </Grid>

              {/* Pasajeros */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 2 }}>Pasajeros</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    <TextField
                      label="Adultos"
                      type="number"
                      value={datosFormulario.adultos}
                      onChange={(e) => manejarCambioInput('adultos', parseInt(e.target.value) || 0)}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Niños"
                      type="number"
                      value={datosFormulario.ninos}
                      onChange={(e) => manejarCambioInput('ninos', parseInt(e.target.value) || 0)}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <TextField
                      label="Infantes"
                      type="number"
                      value={datosFormulario.infantes}
                      onChange={(e) => manejarCambioInput('infantes', parseInt(e.target.value) || 0)}
                      fullWidth
                      inputProps={{ min: 0 }}
                    />
                  </Grid>
                </Grid>
                {errores.pasajeros && <Alert severity="error" sx={{ mt: 1 }}>{errores.pasajeros}</Alert>}
              </Grid>

              {/* Descripción */}
              <Grid item xs={12}>
                <TextField
                  label="Descripción del paquete"
                  multiline
                  rows={3}
                  value={datosFormulario.descripcion}
                  onChange={(e) => manejarCambioInput('descripcion', e.target.value)}
                  fullWidth
                  placeholder="Ej: Incluye vuelo, hotel y excursión"
                />
              </Grid>

              {/* Sección Aéreo, bus o crucero */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Aéreo, bus o crucero</Typography>
                <ServiceTable
                  type="aereo"
                  services={serviciosAereos}
                  onAdd={agregarServicioAereo}
                  onUpdate={actualizarServicioAereo}
                  onDelete={eliminarServicioAereo}
                  opcionesProveedores={opcionesProveedores}
                  opcionesEspecificas={opcionesAereolineas}
                />
              </Grid>

              {/* Sección Alojamiento */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Alojamiento</Typography>
                <ServiceTable
                  type="alojamiento"
                  services={serviciosAlojamiento}
                  onAdd={agregarServicioAlojamiento}
                  onUpdate={actualizarServicioAlojamiento}
                  onDelete={eliminarServicioAlojamiento}
                  opcionesProveedores={opcionesProveedores}
                  opcionesEspecificas={opcionesHoteles}
                />
              </Grid>

              {/* Sección Servicios */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 3 }}>Servicios</Typography>
                <ServiceTable
                  type="extra"
                  services={serviciosExtras}
                  onAdd={agregarServicioExtra}
                  onUpdate={actualizarServicioExtra}
                  onDelete={eliminarServicioExtra}
                  opcionesProveedores={opcionesProveedores}
                  opcionesEspecificas={opcionesProveedores}
                />
              </Grid>

              {/* Totales */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    <Typography variant="h6">Importe total</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="$"
                        value={totalARS}
                        onChange={(e) => setTotalARS(parseFloat(e.target.value) || 0)}
                        type="number"
                        size="small"
                        sx={{ width: 120 }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                      <TextField
                        label="U$S"
                        value={totalUSD}
                        onChange={(e) => setTotalUSD(parseFloat(e.target.value) || 0)}
                        type="number"
                        size="small"
                        sx={{ width: 120 }}
                        inputProps={{ min: 0, step: 0.01 }}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    <Typography variant="subtitle1">Costo total</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <TextField
                        label="$"
                        value={totalCostoARS}
                        size="small"
                        sx={{ width: 120 }}
                        InputProps={{ readOnly: true }}
                      />
                      <TextField
                        label="U$S"
                        value={totalCostoUSD}
                        size="small"
                        sx={{ width: 120 }}
                        InputProps={{ readOnly: true }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={manejarSiguiente}
                    disabled={!esPrimeraPestañaValida}
                    sx={{ backgroundColor: '#461264', '&:hover': { backgroundColor: '#5a1a7a' } }}
                  >
                    Siguiente
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={pestañaActiva} index={1}>
            <Grid container spacing={3}>
              {/* Cliente */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  freeSolo
                  options={[]}
                  value={datosFormulario.cliente}
                  onChange={(event, newValue) => manejarCambioInput('cliente', newValue || '')}
                  onInputChange={(event, newInputValue) => manejarCambioInput('cliente', newInputValue)}
                  renderInput={(params) => (
                    <TextField {...params} label="Cliente" error={!!errores.cliente} helperText={errores.cliente} />
                  )}
                />
              </Grid>
              
              {/* Contacto del cliente */}
              <Grid item xs={12} md={6}>
                <TextField
                  label="Teléfono del cliente"
                  value={datosFormulario.telefonoCliente}
                  onChange={(e) => manejarCambioInput('telefonoCliente', e.target.value)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Correo del cliente"
                  type="email"
                  value={datosFormulario.emailCliente}
                  onChange={(e) => manejarCambioInput('emailCliente', e.target.value)}
                  fullWidth
                />
              </Grid>

              {/* Obra social */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={datosFormulario.obraSocial}
                      onChange={(e) => manejarCambioInput('obraSocial', e.target.checked)}
                    />
                  }
                  label="Obra Social (tildar solo si corresponde)"
                />
                {datosFormulario.obraSocial && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Atención! Tildar solamente cuando sea por Obra Social.
                  </Alert>
                )}
              </Grid>

              {/* Botones de navegación */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setPestañaActiva(0)}
                    sx={{ borderColor: '#461264', color: '#461264' }}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="contained"
                    onClick={confirmarYGuardar}
                    disabled={!datosFormulario.cliente || guardando}
                    sx={{ backgroundColor: '#461264', '&:hover': { backgroundColor: '#5a1a7a' } }}
                    startIcon={guardando ? <CircularProgress size={20} color="inherit" /> : null}
                  >
                    {guardando ? 'Guardando...' : 'Guardar presupuesto'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </TabPanel>
        </Paper>

        {/* Diálogo de confirmación mejorado */}
        <Dialog
          open={mostrarConfirmacion}
          onClose={() => !guardando && setMostrarConfirmacion(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Confirmar creación de paquete
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Revise los datos del paquete antes de confirmar:
            </DialogContentText>
            
            {/* Información del Cliente */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                DATOS DEL CLIENTE
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Cliente:</strong> {datosFormulario.cliente}
                  </Typography>
                </Grid>
                {datosFormulario.telefonoCliente && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Teléfono:</strong> {datosFormulario.telefonoCliente}
                    </Typography>
                  </Grid>
                )}
                {datosFormulario.emailCliente && (
                  <Grid item xs={12}>
                    <Typography variant="body2">
                      <strong>Email:</strong> {datosFormulario.emailCliente}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Información del Viaje */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                DATOS DEL VIAJE
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <Typography variant="body2">
                    <strong>Concepto:</strong> {datosFormulario.concepto}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Origen:</strong> {datosFormulario.origen}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Destino:</strong> {datosFormulario.destino}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Fecha salida:</strong> {datosFormulario.fechaSalida?.format('DD/MM/YYYY') || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2">
                    <strong>Pasajeros:</strong> {datosFormulario.adultos} adultos, {datosFormulario.ninos} niños, {datosFormulario.infantes} infantes
                  </Typography>
                </Grid>
              </Grid>
            </Box>

            {/* Servicios */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                SERVICIOS INCLUIDOS
              </Typography>
              {serviciosAereos.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Aéreos ({serviciosAereos.length}):
                  </Typography>
                  {serviciosAereos.map((s, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                      • {s.aerolinea} - {s.destino} ({s.moneda} {s.precioVenta.toFixed(2)})
                    </Typography>
                  ))}
                </Box>
              )}
              {serviciosAlojamiento.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Alojamientos ({serviciosAlojamiento.length}):
                  </Typography>
                  {serviciosAlojamiento.map((s, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                      • {s.hotel} - {s.destino} ({s.noches} noches - {s.moneda} {s.precioVenta.toFixed(2)})
                    </Typography>
                  ))}
                </Box>
              )}
              {serviciosExtras.length > 0 && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Servicios extras ({serviciosExtras.length}):
                  </Typography>
                  {serviciosExtras.map((s, i) => (
                    <Typography key={i} variant="body2" sx={{ ml: 2 }}>
                      • {s.servicio} ({s.moneda} {s.precioVenta.toFixed(2)})
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>

            {/* Totales */}
            <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1, border: '1px solid', borderColor: 'success.light' }}>
              <Typography variant="subtitle2" color="success.dark" gutterBottom>
                RESUMEN FINANCIERO
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Total Venta:
                  </Typography>
                  {totalARS > 0 && (
                    <Typography variant="body2">$ {totalARS.toFixed(2)}</Typography>
                  )}
                  {totalUSD > 0 && (
                    <Typography variant="body2">U$D {totalUSD.toFixed(2)}</Typography>
                  )}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Total Costo:
                  </Typography>
                  {totalCostoARS > 0 && (
                    <Typography variant="body2">$ {totalCostoARS.toFixed(2)}</Typography>
                  )}
                  {totalCostoUSD > 0 && (
                    <Typography variant="body2">U$D {totalCostoUSD.toFixed(2)}</Typography>
                  )}
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMostrarConfirmacion(false)} disabled={guardando}>
              Cancelar
            </Button>
            <Button 
              onClick={manejarGuardar} 
              variant="contained" 
              disabled={guardando}
              sx={{ backgroundColor: '#461264', '&:hover': { backgroundColor: '#5a1a7a' } }}
              startIcon={guardando ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {guardando ? 'Guardando...' : 'Confirmar y Crear Paquete'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </LocalizationProvider>
  );
};

export default NuevoPresupuesto;